// StepFlow — a workflow rendered as a readable vertical flow diagram.
//
// PatchyHub shows "Total Steps: 24". This shows the 24 steps, in order, with what each one
// actually does and which assets it touches. That is the whole differentiation, so the bar is
// legibility: no auto-layout, no zoom/pan, no canvas. A vertical spine with typed nodes stays
// readable at any length, prints, and needs no chart library (the console ships none by design).
import type { ReactNode } from 'react';

export interface FlowStep {
  id: string;
  order: number;
  type: string;
  type_label?: string;
  name: string | null;
  branch?: string | null;
  summary: string | null;
  links?: Array<{ targetType: string; ref: string; relation: string; byName?: boolean }>;
}

// Step families → tone. Grouped by what the step DOES, because that is how someone scanning a
// workflow thinks ("where does it message? where does it wait? where does it branch?").
const TONES: Array<{ test: RegExp; tone: string; dot: string; label: string }> = [
  { test: /sms|message|email|notification|whatsapp|call|voicemail/i, tone: 'border-sky-200 bg-sky-50', dot: 'bg-sky-500', label: 'Message' },
  { test: /wait|delay|drip|schedule/i, tone: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', label: 'Timing' },
  { test: /if|else|condition|branch|split|goal|filter/i, tone: 'border-violet-200 bg-violet-50', dot: 'bg-violet-500', label: 'Logic' },
  { test: /tag|field|value|contact|note/i, tone: 'border-emerald-200 bg-emerald-50', dot: 'bg-emerald-500', label: 'Data' },
  { test: /opportunity|pipeline|stage|deal/i, tone: 'border-indigo-200 bg-indigo-50', dot: 'bg-indigo-500', label: 'Pipeline' },
  { test: /ai|premium|marketplace|enrich|lookup/i, tone: 'border-rose-200 bg-rose-50', dot: 'bg-rose-500', label: 'Premium' },
  { test: /webhook|http|integration|zapier|slack/i, tone: 'border-slate-300 bg-slate-50', dot: 'bg-slate-500', label: 'Integration' },
];
const toneFor = (type: string) =>
  TONES.find((t) => t.test.test(type)) ?? { tone: 'border-slate-200 bg-white', dot: 'bg-slate-400', label: 'Step' };

const LINK_LABEL: Record<string, string> = {
  tag: 'Tag', workflow: 'Workflow', form: 'Form', survey: 'Survey', funnel: 'Funnel',
  funnel_step: 'Page', pipeline: 'Pipeline', pipeline_stage: 'Stage', calendar: 'Calendar',
  email_template: 'Template', snippet: 'Snippet', custom_field: 'Field', custom_value: 'Value',
  trigger_link: 'Link', product: 'Product', user: 'User', membership_product: 'Offer',
  custom_object: 'Object',
};

/**
 * A referenced asset, shown on the node that touches it. Clicking jumps to that asset.
 * Prefers the asset's NAME — "Template · Welcome Email" is what someone reading a workflow needs;
 * a truncated GHL id tells them nothing. Falls back to the id only when we cannot resolve it,
 * which is itself meaningful (that asset is not in this account).
 */
function LinkChip({
  link, onOpen, resolveName,
}: {
  link: { targetType: string; ref: string; byName?: boolean };
  onOpen?: (targetType: string, ref: string) => void;
  resolveName?: (ref: string) => string | null;
}) {
  const label = LINK_LABEL[link.targetType] ?? link.targetType;
  const name = link.byName ? link.ref : resolveName?.(link.ref) ?? null;
  const body = (
    <>
      <span className="text-slate-400">{label}</span>
      {name
        ? <span className="max-w-[14rem] truncate text-slate-700">{name}</span>
        : <span className="font-mono text-[10px] text-slate-400" title={link.ref}>unresolved</span>}
    </>
  );
  const cls = 'inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]';
  return onOpen && name
    ? <button onClick={() => onOpen(link.targetType, link.ref)} className={`${cls} hover:border-brand hover:text-brand`}>{body}</button>
    : <span className={`${cls} ${name ? '' : 'border-dashed'}`}>{body}</span>;
}

function Node({
  index, step, kind, onOpenLink, resolveName,
}: {
  index: number | null;
  step: FlowStep;
  kind: 'trigger' | 'step';
  onOpenLink?: (targetType: string, ref: string) => void;
  resolveName?: (ref: string) => string | null;
}) {
  const tone = toneFor(step.type);
  const isTrigger = kind === 'trigger';
  return (
    <div className={`relative rounded-xl border p-3 ${isTrigger ? 'border-brand/40 bg-brand/5' : tone.tone}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${isTrigger ? 'bg-brand' : tone.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {isTrigger ? 'When' : `${index}. ${step.type_label ?? step.type}`}
            </span>
            {step.branch && (
              <span className="rounded bg-violet-100 px-1.5 py-px text-[10px] font-medium text-violet-700">
                {step.branch}
              </span>
            )}
          </div>
          <div className="truncate text-sm font-medium text-slate-900">{step.name ?? step.type_label ?? step.type}</div>
          {step.summary && <div className="mt-0.5 text-xs leading-relaxed text-slate-600">{step.summary}</div>}
          {!!step.links?.length && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {step.links.slice(0, 6).map((l, i) => (
                <LinkChip key={`${l.targetType}-${l.ref}-${i}`} link={l} onOpen={onOpenLink} resolveName={resolveName} />
              ))}
              {step.links.length > 6 && (
                <span className="text-[11px] text-slate-400">+{step.links.length - 6} more</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Connector = () => (
  <div className="flex justify-center py-1" aria-hidden>
    <div className="h-4 w-px bg-slate-300" />
  </div>
);

export default function StepFlow({
  triggers, steps, onOpenLink, resolveName, empty,
}: {
  triggers: FlowStep[];
  steps: FlowStep[];
  onOpenLink?: (targetType: string, ref: string) => void;
  /** ref (GHL id) → the asset's name, when it is inventoried in this account. */
  resolveName?: (ref: string) => string | null;
  empty?: ReactNode;
}) {
  if (!triggers.length && !steps.length) {
    return <>{empty ?? <p className="py-8 text-center text-sm text-slate-400">No steps captured for this asset.</p>}</>;
  }
  return (
    <div className="mx-auto max-w-xl">
      {triggers.map((t, i) => (
        <div key={t.id}>
          <Node index={null} step={t} kind="trigger" onOpenLink={onOpenLink} resolveName={resolveName} />
          {(i < triggers.length - 1 || steps.length > 0) && <Connector />}
        </div>
      ))}
      {steps.map((s, i) => (
        <div key={s.id}>
          <Node index={i + 1} step={s} kind="step" onOpenLink={onOpenLink} resolveName={resolveName} />
          {i < steps.length - 1 && <Connector />}
        </div>
      ))}
      {!!steps.length && (
        <div className="mt-2 text-center text-[11px] text-slate-400">
          End of workflow · {steps.length} step{steps.length === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}

export { toneFor, LINK_LABEL };
